import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import permissionService from '../../services/permissionService';

// ── Naming Convention Helper ─────────────────────────────────────
const NamingConventionHelper = () => (
  <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }}>
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      Permission Naming Convention
    </Typography>

    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Resource-level — <code>{'{resource}.{action}'}</code>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        <Chip label="products.read"    size="small" variant="outlined" />
        <Chip label="orders.create"    size="small" variant="outlined" />
        <Chip label="users.delete"     size="small" variant="outlined" />
        <Chip label="suppliers.update" size="small" variant="outlined" />
      </Box>
    </Box>

    <Divider sx={{ my: 1 }} />

    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Column read — <code>{'{resource}.view.{column}'}</code>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        <Chip label="products.view.buy_price"  size="small" variant="outlined" color="primary" />
        <Chip label="orders.view.profit"       size="small" variant="outlined" color="primary" />
        <Chip label="users.view.phone"         size="small" variant="outlined" color="primary" />
      </Box>
    </Box>

    <Divider sx={{ my: 1 }} />

    <Box>
      <Typography variant="caption" color="text.secondary">
        Column write — <code>{'{resource}.edit.{column}'}</code>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        <Chip label="products.edit.buy_price" size="small" variant="outlined" color="warning" />
        <Chip label="orders.edit.discount"    size="small" variant="outlined" color="warning" />
      </Box>
    </Box>

    <Divider sx={{ my: 1 }} />

    <Typography variant="caption" color="text.secondary">
      Available resources: <strong>users, roles, permissions, stores, products, categories,
      dashboard, attributes, stock, inventory, orders, shop_orders, suppliers, purchase_orders, customers, promotions, reports</strong>
    </Typography>
  </Alert>
);

const PermissionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    resource: '',
    action: '',
    description: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchPermission();
    }
  }, [id]);

  const fetchPermission = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getPermissionById(id);
      setFormData({
        name: data.name || '',
        displayName: data.displayName || '',
        resource: data.resource || '',
        action: data.action || '',
        description: data.description || '',
        isActive: data.isActive ?? true,
      });
    } catch (err) {
      setError('Failed to load permission');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill resource and action from name
  const handleNameChange = (e) => {
    const name = e.target.value.toLowerCase().replace(/\s/g, '_');
    const parts = name.split('.');

    setFormData(prev => ({
      ...prev,
      name,
      resource: parts[0] || '',
      action: parts.slice(1).join('.') || '',
      // Auto-generate displayName from name
      displayName: prev.displayName || generateDisplayName(name),
    }));
  };

  const generateDisplayName = (name) => {
    return name
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
      .replace(/_/g, ' ');
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEdit) {
        await permissionService.updatePermission(id, formData);
        setSuccess('Permission updated successfully');
      } else {
        await permissionService.createPermission(formData);
        setSuccess('Permission created successfully');
        setTimeout(() => navigate('/permissions'), 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save permission');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={() => navigate('/permissions')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {isEdit ? 'Edit Permission' : 'Create Permission'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left — Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>

              {/* Permission Name */}
              <TextField
                fullWidth
                label="Permission Name *"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                margin="normal"
                required
                disabled={isEdit}
                helperText={
                  formData.name
                    ? `Resource: "${formData.resource}" · Action: "${formData.action}"`
                    : 'e.g. products.read or products.view.buy_price'
                }
                InputProps={{
                  sx: { fontFamily: 'monospace' }
                }}
              />

              {/* Display Name */}
              <TextField
                fullWidth
                label="Display Name *"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                margin="normal"
                required
                helperText="Human-readable name e.g. 'View Products'"
              />

              {/* Resource + Action (auto-filled, read-only) */}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Resource"
                    name="resource"
                    value={formData.resource}
                    onChange={handleChange}
                    margin="normal"
                    helperText="Auto-filled from name"
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Action"
                    name="action"
                    value={formData.action}
                    onChange={handleChange}
                    margin="normal"
                    helperText="Auto-filled from name"
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                </Grid>
              </Grid>

              {/* Description */}
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={2}
              />

              {/* Active */}
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                }
                label="Active"
                sx={{ mt: 1 }}
              />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/permissions')}
                >
                  Cancel
                </Button>
              </Box>

            </Box>
          </Paper>
        </Grid>

        {/* Right — Naming Convention Helper */}
        <Grid item xs={12} md={5}>
          <NamingConventionHelper />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PermissionForm;